import "@nectary/components/inline-alert";
import "@nectary/components/button";
import "@nectary/components/dialog";
import "@nectary/components/text";
import "@nectary/components/checkbox";
import "@nectary/components/link";
import { useState } from "react";
import { useIntl } from "react-intl";
import type { CookieConsentValue } from "./consent";

type CookieBannerProps = {
  closeBanner: () => void;
  updateConsent: (
    acceptance: CookieConsentValue,
    performance: CookieConsentValue,
    targeting: CookieConsentValue
  ) => void;
};

export const CookieBanner = ({
  closeBanner,
  updateConsent,
}: CookieBannerProps) => {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [performanceCookieCheck, setPerformanceCookie] = useState(true);
  const [targetingCookieCheck, setTargetingCookie] = useState(true);

  const intl = useIntl();

  return (
    <>
      <sinch-inline-alert
        type="info"
        caption={intl.formatMessage({ id: "cookieBannerTitle" })}
        text={intl.formatMessage({ id: "cookieBannerBody" })}
        style={{
          position: "fixed",
          bottom: 0,
          right: 0,
          left: 0,
          zIndex: 100,
        }}
      >
        <div slot="action" style={{ display: "flex", gap: 8 }}>
          <sinch-button
            on-click={() => {
              updateConsent("true", "true", "true");
              closeBanner();
            }}
            text={intl.formatMessage({ id: "cookieBannerAllowAllButton" })}
            aria-label={intl.formatMessage({
              id: "cookieBannerAllowAllButton",
            })}
            type="cta-primary"
          />
          <sinch-button
            on-click={() => {
              setDialogOpen(true);
            }}
            text={intl.formatMessage({ id: "cookieBannerOpenDialogButton" })}
            aria-label={intl.formatMessage({
              id: "cookieBannerOpenDialogButton",
            })}
            type="cta-secondary"
          />
        </div>
      </sinch-inline-alert>
      <sinch-dialog
        open={isDialogOpen}
        aria-label={intl.formatMessage({ id: "cookieDialogAria" })}
        caption={intl.formatMessage({ id: "cookieDialogTitle" })}
        close-aria-label={intl.formatMessage({
          id: "cookieDialogClose",
        })}
        on-close={() => setDialogOpen(false)}
        style={{
          "--sinch-comp-dialog-max-width": "850px",
        }}
      >
        <div
          slot="content"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <sinch-text type="m">
            {intl.formatMessage({ id: "cookieDialogIntroduction" })}
          </sinch-text>
          <sinch-link
            external
            // TODO: move to configcat
            href="https://messagemedia.com/uk/cookie-statement/"
            text={intl.formatMessage({ id: "cookieDialogLink" })}
            aria-label={intl.formatMessage({ id: "cookieDialogLinkAria" })}
          />
          <sinch-checkbox
            text={intl.formatMessage({ id: "cookieDialogNecessaryCheckBox" })}
            aria-label={intl.formatMessage({
              id: "cookieDialogNecessaryCheckBox",
            })}
            checked
            disabled
          />
          <sinch-text type="s">
            {intl.formatMessage({
              id: "cookieDialogNecessaryBody",
            })}
          </sinch-text>
          <sinch-checkbox
            text={intl.formatMessage({
              id: "cookieDialogPerformanceCheckBox",
            })}
            aria-label={intl.formatMessage({
              id: "cookieDialogPerformanceCheckBox",
            })}
            checked={performanceCookieCheck}
            on-change={(e) => setPerformanceCookie(e.detail)}
          />
          <sinch-text type="s">
            {intl.formatMessage({
              id: "cookieDialogPerformanceBody",
            })}
          </sinch-text>
          <sinch-checkbox
            text={intl.formatMessage({
              id: "cookieDialogTargetingCheckBox",
            })}
            aria-label={intl.formatMessage({
              id: "cookieDialogTargetingCheckBox",
            })}
            checked={targetingCookieCheck}
            on-change={(e) => setTargetingCookie(e.detail)}
          />
          <sinch-text type="s">
            {intl.formatMessage({
              id: "cookieDialogTargetingBody",
            })}
          </sinch-text>
        </div>
        <div slot="buttons" style={{ display: "flex", gap: 8 }}>
          <sinch-button
            on-click={() => {
              updateConsent("true", "true", "true");
              closeBanner();
              setDialogOpen(false);
            }}
            text={intl.formatMessage({ id: "cookieBannerAllowAllButton" })}
            aria-label={intl.formatMessage({
              id: "cookieBannerAllowAllButton",
            })}
            type="cta-primary"
          />
          <sinch-button
            on-click={() => {
              setDialogOpen(false);
              updateConsent(
                "true",
                `${performanceCookieCheck}`,
                `${targetingCookieCheck}`
              );
              closeBanner();
              setDialogOpen(false);
            }}
            text={intl.formatMessage({
              id: "cookieBannerConfirmButton",
            })}
            aria-label={intl.formatMessage({
              id: "cookieBannerConfirmButton",
            })}
            type="cta-secondary"
          />
        </div>
      </sinch-dialog>
    </>
  );
};
